// @ts-nocheck
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMantineTheme, useMantineColorScheme } from '@mantine/core';
import { Alert, Anchor, Badge, Box, Button, Card, Center, Container, Stack, Text, TextInput, Title } from '@/components/ui';
import { IconMail, IconArrowLeft, IconKey, IconCheck, IconSparkles } from '@tabler/icons-react';
import { FadeIn, SlideIn, ScaleIn, HoverLift, useReducedMotion } from '@/components/animations';
import { AnimatedFormWrapper } from '../components';

export default function ForgotAnimated() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const reduceMotion = useReducedMotion();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const successContent = (
    <Stack gap="lg" align="center">
      <ScaleIn delay={100}>
        <Center
          style={{
            width: 80,
            height: 80,
            borderRadius: 20,
            backgroundColor: 'var(--mantine-color-green-light)',
          }}
        >
          <IconCheck size={40} color="var(--mantine-color-green-6)" />
        </Center>
      </ScaleIn>

      <FadeIn delay={200}>
        <Stack align="center" gap="xs">
          <Title order={2} ta="center">Check your email</Title>
          <Text c="dimmed" ta="center" maw={300}>
            We've sent a password reset link to <strong>{email}</strong>
          </Text>
        </Stack>
      </FadeIn>

      <SlideIn direction="up" delay={300}>
        <Alert color="blue" variant="light" w="100%">
          <Text size="sm">
            Didn't receive the email? Check your spam folder or{' '}
            <Anchor onClick={() => setSubmitted(false)}>try again</Anchor>
          </Text>
        </Alert>
      </SlideIn>

      <FadeIn delay={400}>
        <Button
          component={Link}
          to="/demo/auth-pages/login/animated"
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
        >
          Back to login
        </Button>
      </FadeIn>
    </Stack>
  );

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: isDark
          ? `linear-gradient(135deg, ${theme.colors.dark[8]} 0%, ${theme.colors.dark[9]} 100%)`
          : `linear-gradient(135deg, ${theme.colors.gray[0]} 0%, ${theme.colors.orange[0]} 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated background elements */}
      <Box style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        {[...Array(4)].map((_, i) => (
          <Box
            key={i}
            style={{
              position: 'absolute',
              width: 100 + i * 40,
              height: 100 + i * 40,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${theme.colors.orange[isDark ? 8 : 3]}, ${theme.colors.yellow[isDark ? 8 : 3]})`,
              opacity: 0.1,
              top: `${15 + i * 20}%`,
              left: `${10 + i * 15}%`,
              animation: reduceMotion ? 'none' : `float ${4 + i}s ease-in-out infinite`,
              animationDelay: `${i * 0.4}s`,
            }}
          />
        ))}
      </Box>

      <Center mih="100vh" p="md" style={{ position: 'relative', zIndex: 1 }}>
        <Container size="xs" w="100%">
          <ScaleIn delay={100} duration={500}>
            <HoverLift>
              <Card variant="elevated" p="xl" radius="lg">
                {submitted ? (
                  successContent
                ) : (
                  <Stack gap="lg">
                    {/* Back button */}
                    <SlideIn direction="left" delay={200}>
                      <Button
                        component={Link}
                        to="/demo/auth-pages/forgot-password"
                        variant="subtle"
                        size="xs"
                        leftSection={<IconArrowLeft size={14} />}
                        w="fit-content"
                      >
                        Back to variants
                      </Button>
                    </SlideIn>

                    {/* Header */}
                    <FadeIn delay={300}>
                      <Stack align="center" gap="xs">
                        <Badge
                          leftSection={<IconSparkles size={12} />}
                          variant="gradient"
                          gradient={{ from: 'orange', to: 'yellow' }}
                          size="lg"
                        >
                          Animated Experience
                        </Badge>
                        <Center
                          style={{
                            width: 56,
                            height: 56,
                            borderRadius: 14,
                            backgroundColor: 'var(--mantine-color-orange-light)',
                            marginTop: 'var(--mantine-spacing-sm)',
                          }}
                        >
                          <IconKey size={28} color="var(--mantine-color-orange-6)" />
                        </Center>
                        <Title order={2} ta="center" mt="sm">
                          Forgot password?
                        </Title>
                        <Text c="dimmed" size="sm" ta="center" maw={300}>
                          No worries, we'll send you reset instructions.
                        </Text>
                      </Stack>
                    </FadeIn>

                    {/* Form */}
                    <form onSubmit={handleSubmit}>
                      <AnimatedFormWrapper animation="stagger" delay={400} staggerDelay={100}>
                        <TextInput
                          label="Email"
                          placeholder="Enter your email"
                          leftSection={<IconMail size={16} />}
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          size="md"
                          radius="md"
                        />

                        <Button
                          type="submit"
                          fullWidth
                          size="md"
                          radius="md"
                          variant="gradient"
                          gradient={{ from: 'orange', to: 'yellow' }}
                        >
                          Send reset link
                        </Button>
                      </AnimatedFormWrapper>
                    </form>

                    {/* Back to Login */}
                    <FadeIn delay={600}>
                      <Center>
                        <Anchor component={Link} to="/demo/auth-pages/login/animated" size="sm">
                          <IconArrowLeft size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                          Back to login
                        </Anchor>
                      </Center>
                    </FadeIn>
                  </Stack>
                )}
              </Card>
            </HoverLift>
          </ScaleIn>
        </Container>
      </Center>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
      `}</style>
    </Box>
  );
}
